import { router } from "@inertiajs/react"

import { resolvePage } from "@/lib/inertia-page-client"
import { prefetchAllowed } from "@/lib/prefetch"
import { warmHeroFromPage } from "@/lib/prefetch-hero"
import {
  intendedPrefetchedPage,
  noteWarmIntent,
} from "@/lib/prefetch-warm-intent"

const warmed = new Set<string>()

/**
 * Load a page's code-split chunk before the tap. A prefetch only stores the
 * JSON; the chunk was the rest of a tab switch (about one Slow 4G round
 * trip, while drawing the page was under 100ms).
 *
 * This module is imported by the browser entry only. The page resolver pulls
 * every page into its graph, and the server render must not load that.
 */
function warmComponent(name: string) {
  if (!prefetchAllowed() || warmed.has(name)) return
  warmed.add(name)
  void resolvePage(name).catch(() => {
    warmed.delete(name)
  })
}

/** Warm common destination code before its JSON prefetch has returned. */
export function warmTubeHref(href: string) {
  noteWarmIntent(href)
  const path = new URL(href, window.location.origin).pathname
  const name =
    path === "/"
      ? "today/show"
      : path === "/videos"
        ? "videos/index"
        : path === "/watch"
          ? "watches/show"
          : path === "/search"
            ? "searches/show"
            : path === "/clips"
              ? "clips/index"
              : path.startsWith("/songs/")
                ? "songs/show"
                : path.startsWith("/orchestras/")
                  ? "orchestras/show"
                  : path.startsWith("/dancers/")
                    ? "dancers/show"
                    : null
  if (name) warmComponent(name)
}

function componentName(data: unknown): string | null {
  const parsed = typeof data === "string" ? safeParse(data) : data
  if (!parsed || typeof parsed !== "object" || !("component" in parsed)) {
    return null
  }
  const name = parsed.component
  return typeof name === "string" ? name : null
}

function safeParse(data: string): unknown {
  try {
    return JSON.parse(data)
  } catch {
    return null
  }
}

/**
 * Warm the page named by a finished navigation prefetch. Loading four phone
 * destinations immediately after hydration made each first visit fetch the
 * code for pages the reader had not asked to see.
 */
export function warmPhonePages() {
  router.on("prefetched", (event) => {
    const page = intendedPrefetchedPage(event.detail.response.data)
    if (!page) return
    if (prefetchAllowed()) warmHeroFromPage(page)
    const name = componentName(page)
    if (name) warmComponent(name)
  })
}
